import axios from 'axios';
import { API_BASE_URL } from '../../constants/config';
import { getAndSanitize } from '../../shared/utils/sanitization';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/gamification`,
});

// Configurar el interceptor para incluir el token y adminId en todas las peticiones
api.interceptors.request.use((config) => {
  const token = getAndSanitize('token');
  const role = getAndSanitize('role');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Auto-inject adminId based on role
  if (role === 'SUPERADMIN') {
    const selectedAdmin = localStorage.getItem('superadmin_selected_admin');
    if (selectedAdmin && selectedAdmin !== 'null') {
      config.params = { ...config.params, adminId: selectedAdmin };
    }
    // If no admin selected, don't send adminId — backend handles SUPERADMIN with null
  } else if (role === 'ADMIN') {
    const userId = getAndSanitize('userId');
    if (userId) {
      config.params = { ...config.params, adminId: userId };
    }
  }

  return config;
});

export const gamificationApi = {
  // Bonus Preview
  getBonusPreview: async (userId: string, amount: number) => {
    const response = await api.get('/bonus-preview', { params: { userId, amount } });
    return response.data;
  },

  // Config endpoints
  getConfig: async () => {
    const response = await api.get('/config');
    return response.data;
  },

  updateConfig: async (data: any) => {
    const response = await api.put('/config', data);
    return response.data;
  },

  // Levels endpoints
  getLevels: async () => {
    const response = await api.get('/levels');
    return response.data;
  },

  createLevel: async (data: any) => {
    const response = await api.post('/levels', data);
    return response.data;
  },

  updateLevel: async (id: string, data: any) => {
    const response = await api.put(`/levels/${id}`, data);
    return response.data;
  },

  deleteLevel: async (id: string) => {
    const response = await api.delete(`/levels/${id}`);
    return response.data;
  },

  // Rules endpoints
  getRules: async () => {
    const response = await api.get('/rules');
    return response.data;
  },

  getRule: async (id: string) => {
    const response = await api.get(`/rules/${id}`);
    return response.data;
  },

  createRule: async (data: any) => {
    const response = await api.post('/rules', data);
    return response.data;
  },

  updateRule: async (id: string, data: any) => {
    const response = await api.put(`/rules/${id}`, data);
    return response.data;
  },

  deleteRule: async (id: string) => {
    const response = await api.delete(`/rules/${id}`);
    return response.data;
  },

  // Players endpoints
  getPlayers: async (levelFilter?: number | null) => {
    const response = await api.get('/players', {
      params: levelFilter ? { level: levelFilter } : {}
    });
    return response.data;
  },

  // User detailed statistics
  getUserReferralStats: async (userId: string) => {
    const response = await api.get(`/users/${userId}/referral-stats`);
    return response.data;
  },

  getUserStreakStats: async (userId: string) => {
    const response = await api.get(`/users/${userId}/streak-stats`);
    return response.data;
  },

  getUserLevelProgression: async (userId: string) => {
    const response = await api.get(`/users/${userId}/level-progression`);
    return response.data;
  },

  // Global statistics
  getGlobalStats: async () => {
    const response = await api.get('/stats/global');
    return response.data;
  },

  // ==================== ROLLOVER ====================

  getUserActiveRollover: async (userId: string) => {
    const response = await api.get(`/users/${userId}/rollover`);
    return response.data;
  },

  getAdminRollovers: async (status?: string) => {
    const response = await api.get('/rollovers', {
      params: status ? { status } : {}
    });
    return response.data;
  },

  cancelRollover: async (rolloverId: string) => {
    const response = await api.post(`/rollovers/${rolloverId}/cancel`);
    return response.data;
  },

  cancelRolloverRequirement: async (rolloverId: string) => {
    const response = await api.post(`/rollovers/${rolloverId}/cancel-rollover`);
    return response.data;
  },

  cancelAntiLavadoRequirement: async (rolloverId: string) => {
    const response = await api.post(`/rollovers/${rolloverId}/cancel-antilavado`);
    return response.data;
  },

  // ==================== EXPORT/IMPORT ====================

  // Export gamification configuration
  exportConfig: async () => {
    const response = await api.get('/export');
    return response.data;
  },

  // Preview import data
  previewImport: async (data: any) => {
    const response = await api.post('/import/preview', { data });
    return response.data;
  },

  // Import gamification configuration
  importConfig: async (data: any, options: {
    mode: 'replace' | 'merge';
    includeConfig: boolean;
    includeLevels: boolean;
    includeRules: boolean;
    includeObjectives: boolean;
    includeRedemptions: boolean;
  }) => {
    const response = await api.post('/import', { data, options });
    return response.data;
  },
};
